import type { MaterialPresetItem } from "../../../shared/api-contract";

/**
 * Sphere swatches drawn in CSS, for presets that have no rendered thumbnail —
 * the viewer's built-in metals, stones, ceramics and pearls carry only a
 * colour (or nothing), and a flat dot reads as a paint chip, not a material.
 * Mirrored presets ship real renders and never come here.
 */

type Rgb = [number, number, number];

const rgbOf = (n: number): Rgb => [(n >> 16) & 255, (n >> 8) & 255, n & 255];
const css = ([r, g, b]: Rgb) => `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
const lighten = (c: Rgb, k: number): Rgb => c.map((v) => v + (255 - v) * k) as Rgb;
const darken = (c: Rgb, k: number): Rgb => c.map((v) => v * k) as Rgb;

/** A soft, lit-metal sphere for a flat colour — how a metal swatch should read. */
export function metalFill(n: number): string {
  const c = rgbOf(n);
  return `radial-gradient(circle at 35% 30%, ${css(lighten(c, 0.9))} 0, ${css(lighten(c, 0.45))} 30%, ${css(darken(c, 0.93))} 64%, ${css(darken(c, 0.66))} 100%)`;
}

/** A gem: bright table, coloured body, dark girdle edge. */
export function gemFill(n: number): string {
  const c = rgbOf(n);
  return `radial-gradient(circle at 35% 30%, #ffffff 0, ${css(lighten(c, 0.55))} 28%, ${css(darken(c, 0.82))} 64%, ${css(darken(c, 0.45))} 100%)`;
}

/** Glazed and opaque: a tight white highlight on a solid body. */
function ceramicFill(n: number): string {
  const c = rgbOf(n);
  return `radial-gradient(circle at 33% 27%, #ffffff 0, ${css(lighten(c, 0.5))} 9%, ${css(c)} 30%, ${css(darken(c, 0.78))} 72%, ${css(darken(c, 0.5))} 100%)`;
}

/** Nacre: a broad soft sheen with a faint rose-green lustre at the rim. */
function pearlFill(n: number): string {
  const c = rgbOf(n);
  return `radial-gradient(circle at 36% 30%, #ffffff 0, ${css(lighten(c, 0.6))} 24%, ${css(c)} 55%, ${css(darken([c[0], c[1] * 0.97, c[2] * 1.02], 0.8))} 85%, ${css(darken(c, 0.66))} 100%)`;
}

/**
 * The viewer's own stones, as a jeweller would picture them. Keyed by the
 * viewer's preset id (`DIAMOND_PRESETS`); a stone not listed falls back to a
 * clear diamond.
 */
export const GEM_FILL: Record<string, string> = {
  diamondClear: "radial-gradient(circle at 35% 30%, #ffffff 0, #eef3f7 30%, #b9c3cc 65%, #7c8793 100%)",
  diamondClear2: "radial-gradient(circle at 35% 30%, #ffffff 0, #eef3f7 30%, #b9c3cc 65%, #7c8793 100%)",
  diamondWarm: "radial-gradient(circle at 35% 30%, #fffdf6 0, #f6ead2 32%, #d6c29b 66%, #9a865f 100%)",
  ruby: "radial-gradient(circle at 35% 30%, #ffc2cc 0, #e0314f 32%, #9d0f2b 68%, #560717 100%)",
  sapphire: "radial-gradient(circle at 35% 30%, #cfe0ff 0, #3a64d8 34%, #1a3596 68%, #0b1b55 100%)",
  emerald: "radial-gradient(circle at 35% 30%, #c9f4dc 0, #22a466 32%, #0f6b3f 68%, #063a22 100%)",
};

const NEUTRAL = 0xc9c9c6;

/**
 * The sphere for a library material with no thumbnail, by what it is. A file
 * the workspace added is drawn by its FORMAT (a .dmat is a stone wherever it is
 * filed) in the colour the file declares.
 */
export function sphereFill(p: { id: string; category: MaterialPresetItem["category"]; color?: number; format?: "pmat" | "dmat" }): string {
  if (p.format === "dmat") return p.color != null ? gemFill(p.color) : GEM_FILL.diamondClear;
  switch (p.category) {
    case "gem":
      return GEM_FILL[p.id] ?? (p.color != null ? gemFill(p.color) : GEM_FILL.diamondClear);
    case "ceramic":
      return ceramicFill(p.color ?? NEUTRAL);
    case "pearl":
      return pearlFill(p.color ?? 0xf3efe6);
    case "other":
      return p.format ? metalFill(p.color ?? NEUTRAL) : ceramicFill(p.color ?? NEUTRAL);
    default:
      return metalFill(p.color ?? NEUTRAL);
  }
}

/**
 * An environment or backdrop with no picture yet: a neutral chrome ball, the
 * object photographers light-test with, so a missing thumbnail still reads as
 * "a lighting preset" rather than a broken image.
 */
export const LIGHT_BALL = metalFill(0xd9d9d6);
